import React from "react";
import client from "@culturehq/client";
import { Icon, Loader, Panel } from "@culturehq/components";
import tagsTypes from "constants/tagsTypes";

import { useProfile } from "app/context";
import { useFetch, useFieldsLabels } from "app/hooks";
import { getFilledIcon, getName, Trans } from "app/utils";

import UserCommonalities from "components/community/UserCommonalities";
import styles from "./styles.module.scss";

const CommonalityGroup = ({ category, group }) => {
  const { profile: { organization: { name } } } = useProfile();
  const labels = useFieldsLabels();

  const getGroupName = groupLength => {
    const isPlural = groupLength > 1;
    switch (category) {
      case tagsTypes.CONNECTION:
        return `mutual ${isPlural ? "people" : "person"} followed:`;
      case tagsTypes.INTEREST:
        return `shared ${isPlural ? "interests" : "interest"}:`;
      case tagsTypes.LOCATION:
        return `shared ${isPlural ? "locations" : "location"}:`;
      case tagsTypes.DEPARTMENT:
        return `shared ${(isPlural ? labels.departments.plural : labels.departments.singular).toLowerCase()}`;
      case tagsTypes.SKILL:
        return `shared ${isPlural ? "skills" : "skill"}:`;
      case tagsTypes.CLASS_YEAR:
        if (name === "Dartmouth Guarini") {
          return "shared years in program:";
        }
        return `shared ${isPlural ? "class years" : "class year"}:`;
      case tagsTypes.CLUB:
        return `shared ${isPlural ? "clubs" : "club"}:`;
      case tagsTypes.PROGRAM:
        return `shared ${isPlural ? "programs" : "program"}:`;
      case tagsTypes.DEGREE:
        return `shared ${isPlural ? "degrees" : "degree"}:`;
      default:
        break;
    }
    return "";
  };

  const getPhrase = (id, name, index) => {
    const PENULTIMATE_INDEX = group.length - 2;
    const LAST_INDEX = group.length - 1;
    if (index === PENULTIMATE_INDEX && group.length < 3) {
      return (<><strong key={id}>{name}</strong>{" and "}</>);
    }

    if (index !== LAST_INDEX) {
      return (<strong key={id}>{name}, </strong>);
    }

    return (<strong key={id}>{name}</strong>);
  };

  const getAndMoreLegend = () => {
    const NUMBER_EXTRA_ITEMS = group.length - 3;
    return NUMBER_EXTRA_ITEMS === 1
      ? `and ${NUMBER_EXTRA_ITEMS} other`
      : `and ${NUMBER_EXTRA_ITEMS} others`;
  };

  return (
    <div key={category} className="Commonality--Category">
      <Icon className="Commonality-Icon" icon={getFilledIcon(category)} />
      <p>
        {group.length} {getGroupName(group.length)}{" "}
        <span>
          {group.map(({ id, name }, index) => (
            index < 3 ? getPhrase(id, name, index) : ""
          ))}
          {group.length > 3 ? getAndMoreLegend() : ""}
        </span>
      </p>
    </div>
  );
};

const Commonalities = ({ user }) => {
  const { fetching, json: { commonEntities = [] } } = useFetch(
    () => client.listUserCommonEntities({ userId: user.id }),
    [user]
  );

  const commonalities = {};
  commonEntities.forEach(commonality => {
    const { type } = commonality;
    if (!commonalities[type]) {
      commonalities[type] = [];
    }
    commonalities[type].push(commonality);
  });

  return commonEntities.length === 0
    ? (<></>)
    : (
      <Loader loading={fetching}>
        <div className="Person--Commonalities">
          <Panel className={styles["panel-inner"]}>
            <Panel.Heading>
              <Icon icon="rings" /><Trans>In common with {getName(user)}</Trans>
            </Panel.Heading>
            <Panel.Body>
              {Object.keys(commonalities).map(key => (
                <CommonalityGroup key={key} category={key} group={commonalities[key]} />
              ))}
            </Panel.Body>
            <Panel.Footer>
              <UserCommonalities user={user} label="View all things in common" />
            </Panel.Footer>
          </Panel>
        </div>
      </Loader>
    );
};

export default Commonalities;
